import type { LeadType, ObjectType, UserType } from '@econtractor/types';

export type BoardFilterUserSnapshot = Pick<
    UserType,
    'ID' | 'userName' | 'userEmail' | 'userPicture'
>;

export type BoardFilterLeadSnapshot = {
    ID: number;
    clientName: string;
    clientPhone?: string;
    clientEmail?: string;
    leadStatus?: number;
};

export type BoardFilterObjectSnapshot = {
    ID: number;
    objectName: string;
    clientName?: string;
    teamName?: string;
};

export type BoardFiltersStored = {
    status: number[] | null;
    type: number[] | null;
    executor: number[] | null;
    controller: number[] | null;
    /** Snapshot для UI (один користувач зараз; масив — на майбутнє) */
    executorUsers: BoardFilterUserSnapshot[] | null;
    controllerUsers: BoardFilterUserSnapshot[] | null;
    leadId: number | null;
    objectId: number | null;
    lead: BoardFilterLeadSnapshot | null;
    object: BoardFilterObjectSnapshot | null;
    onlyPersonal: boolean;
    onlyMine: boolean;
};

const emptyFilters = (): BoardFiltersStored => ({
    status: null,
    type: null,
    executor: null,
    controller: null,
    executorUsers: null,
    controllerUsers: null,
    leadId: null,
    objectId: null,
    lead: null,
    object: null,
    onlyPersonal: false,
    onlyMine: true,
});

const storageKey = (userId: number): string => `econtractor:kanbanFilters:${userId}`;

const normalizeIds = (value: unknown): number[] | null => {
    if (!Array.isArray(value)) return null;
    const ids = value.map(Number).filter((id) => Number.isFinite(id) && id > 0);
    return ids.length ? ids : null;
};

const normalizeId = (value: unknown): number | null => {
    const id = Number(value || 0);
    return Number.isFinite(id) && id > 0 ? id : null;
};

const normalizeUsers = (value: unknown): BoardFilterUserSnapshot[] | null => {
    if (!Array.isArray(value)) return null;
    const users = value
        .map((item) => {
            if (!item || typeof item !== 'object') return null;
            const row = item as Record<string, unknown>;
            const id = Number(row.ID || 0);
            if (!id) return null;
            return {
                ID: id,
                userName: String(row.userName || ''),
                userEmail: String(row.userEmail || ''),
                userPicture: String(row.userPicture || ''),
            } as BoardFilterUserSnapshot;
        })
        .filter(Boolean) as BoardFilterUserSnapshot[];
    return users.length ? users : null;
};

const normalizeLead = (value: unknown): BoardFilterLeadSnapshot | null => {
    if (!value || typeof value !== 'object') return null;
    const row = value as Record<string, unknown>;
    const id = Number(row.ID || 0);
    if (!id) return null;
    return {
        ID: id,
        clientName: String(row.clientName || ''),
        clientPhone: String(row.clientPhone || ''),
        clientEmail: String(row.clientEmail || ''),
        leadStatus: Number(row.leadStatus || 0),
    };
};

const normalizeObject = (value: unknown): BoardFilterObjectSnapshot | null => {
    if (!value || typeof value !== 'object') return null;
    const row = value as Record<string, unknown>;
    const id = Number(row.ID || 0);
    if (!id) return null;
    return {
        ID: id,
        objectName: String(row.objectName || ''),
        clientName: String(row.clientName || ''),
        teamName: String(row.teamName || ''),
    };
};

export const toUserSnapshot = (user: UserType | null): BoardFilterUserSnapshot | null => {
    if (!user?.ID) return null;
    return {
        ID: Number(user.ID),
        userName: String(user.userName || ''),
        userEmail: String(user.userEmail || ''),
        userPicture: String(user.userPicture || ''),
    };
};

export const toLeadSnapshot = (lead: LeadType | null): BoardFilterLeadSnapshot | null => {
    if (!lead?.ID) return null;
    return {
        ID: Number(lead.ID),
        clientName: String(lead.clientName || ''),
        clientPhone: String(lead.clientPhone || ''),
        clientEmail: String(lead.clientEmail || ''),
        leadStatus: Number(lead.leadStatus || 0),
    };
};

export const toObjectSnapshot = (object: ObjectType | null): BoardFilterObjectSnapshot | null => {
    if (!object?.ID) return null;
    return {
        ID: Number(object.ID),
        objectName: String(object.objectName || ''),
        clientName: String(object.clientName || ''),
        teamName: String(object.teamName || ''),
    };
};

export const loadBoardFilters = (
    userId: number,
    options?: { excludeStatuses?: number[] },
): BoardFiltersStored => {
    if (!userId || typeof window === 'undefined') return emptyFilters();

    try {
        const raw = window.localStorage.getItem(storageKey(userId));
        if (!raw) return emptyFilters();
        const parsed = JSON.parse(raw) as Partial<BoardFiltersStored>;
        let status = normalizeIds(parsed?.status);
        const type = normalizeIds(parsed?.type);
        const executor = normalizeIds(parsed?.executor);
        const controller = normalizeIds(parsed?.controller);
        const executorUsers = normalizeUsers(parsed?.executorUsers);
        const controllerUsers = normalizeUsers(parsed?.controllerUsers);
        const onlyPersonal = Boolean(parsed?.onlyPersonal);
        const hasOtherUser = Boolean(
            executor?.some((id) => id !== userId) || controller?.some((id) => id !== userId),
        );
        const onlyMine = hasOtherUser
            ? false
            : parsed?.onlyMine === undefined
              ? true
              : Boolean(parsed.onlyMine);
        const lead = onlyPersonal ? null : normalizeLead(parsed?.lead);
        const object = onlyPersonal ? null : normalizeObject(parsed?.object);
        const leadId = onlyPersonal ? null : normalizeId(parsed?.leadId) || lead?.ID || null;
        const objectId = onlyPersonal ? null : normalizeId(parsed?.objectId) || object?.ID || null;

        if (status && options?.excludeStatuses?.length) {
            const exclude = new Set(options.excludeStatuses.map(Number));
            status = status.filter((id) => !exclude.has(id));
            if (!status.length) status = null;
        }

        return {
            status,
            type,
            executor,
            controller,
            executorUsers,
            controllerUsers,
            leadId,
            objectId,
            lead,
            object,
            onlyPersonal,
            onlyMine,
        };
    } catch {
        return emptyFilters();
    }
};

export const saveBoardFilters = (userId: number, filters: BoardFiltersStored): void => {
    if (!userId || typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(storageKey(userId), JSON.stringify(filters));
    } catch {
        /* ignore quota / private mode */
    }
};

export const clearBoardFilters = (userId: number): void => {
    if (!userId || typeof window === 'undefined') return;
    try {
        window.localStorage.removeItem(storageKey(userId));
    } catch {
        /* ignore */
    }
};
